import { DATE_FORMAT } from "@cinnamon/design-system/constants";

export const TENANT_ID_PARAM = "tenant_id";

/**
 * Array of search params that are automatically injected by the app system.
 * These params are present on every authenticated page and should be excluded
 * when checking if search params are meaningful (e.g., for API queries).
 */
export const SYSTEM_INJECTED_SEARCH_PARAMS = [TENANT_ID_PARAM];

export type DefaultLanguageChangeType = "browser" | "server";

export const LANGUAGE_CODE: Record<string, LanguageCode> = {
  ENGLISH: "en",
  JAPANESE: "ja",
  DEFAULT: "default",
};

export interface ILanguageChange {
  lang: keyof typeof LANGUAGE_CODE;
  defaultLangType?: DefaultLanguageChangeType;
}

// Use type string for unknown language code
export type LanguageCode = "en" | "ja" | string;

export const LANGUAGE_TEXT = {
  [LANGUAGE_CODE.ENGLISH]: "English",
  [LANGUAGE_CODE.JAPANESE]: "日本語",
};

const APP_DOMAIN = window.API_ROOT || import.meta.env.VITE_API_DOMAIN;
const WINDOW_ENABLE_LOGGER_IN_PROD = window.ENABLE_LOGGER_IN_PROD;
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_NAME,
  LS_AUTH_DATA: `${import.meta.env.VITE_NAME}_auth_data`,
  LS_LANGUAGE: "language",
  API_ROOT: `${APP_DOMAIN}/api/v1`,
  AUTH_API_ROOT: `${APP_DOMAIN}/api/auth`,
  AUTH_CALLBACK_URL: `${APP_DOMAIN}/auth/callback`,
  DEFAULT_LANGUAGE: LANGUAGE_CODE.JAPANESE,
  UPLOAD_FILE_SUPPORT: [".pdf"],
  UPLOAD_QUEUE_LENGTH: 3,
  // 50 MB
  UPLOAD_MAX_SIZE: 52428800,
  DEFAULT_DOWNLOAD_FILE_NAME: "download",
  DEFAULT_DOWNLOAD_FILES_NAME: "downloads",
  DATE_FORMAT: DATE_FORMAT.YYYY_MM_DD_HH_MM_SLASH,
  USER: {
    PASSWORD_MIN_LENGTH: 5,
  },
  APP_ENV: window.APP_ENV || import.meta.env.VITE_APP_ENV,
  APP_VERSION: window.APP_VERSION || import.meta.env.VITE_APP_VERSION,
  ENABLE_LOGGER_IN_PROD: (WINDOW_ENABLE_LOGGER_IN_PROD || "") === "true",
  FORCE_DISABLE_FEATURE_FLAGS:
    window.FORCE_DISABLE_FEATURE_FLAGS || import.meta.env.VITE_FORCE_DISABLE_FEATURE_FLAGS || "",
};

const FSH_FLAG_VERSION = "FSH2606";

export const API_PATH = {
  TENANT_PUBLIC_CONFIG: (tenantId: string): string => `/tenants/${tenantId}/public-config/`,
  LOGIN: "/auth/access/",
  LOGIN_AZURE: "/auth/sso/azure/authorize/",
  LOGIN_AZURE_CALLBACK: "/auth/sso/azure/callback/",
  REFRESH: "/auth/refresh/",
  USER_INFO: `${APP_CONFIG.AUTH_API_ROOT}/me/`,
  TWO_FA_SEND_OTP: "/auth/2fa/send-otp/",
  TWO_FA_VERIFY_OTP: "/auth/2fa/verify-otp/",
  WORKFLOWS_LIST: "/workflows",
  WORKFLOW: (id: string): string => `/workflows/${id}/`,
  JOBS: "/jobs/",
  JOBS_BATCH_GET: "/jobs/batch-get/",
  BULK_DELETE_JOB: "/jobs/bulk/",
  JOB_DOWNLOAD: (id: string): string => `/jobs/${id}/result/download/`,
  JOBS_DOWNLOAD_BATCH: "/jobs/result/bulk-download/",
  JOB_TO_LLM_SAMPLE: (id: string): string => `/jobs/${id}/to-llm-sample/`,
  LOCK_JOB: (id: string): string => `/jobs/${id}/lock/`,
  USERS: "/users/",
  BULK_DELETE_USER: "/users/bulk/",
  CHANGE_PASSWORD: `${APP_CONFIG.AUTH_API_ROOT}/me/change-password/`,
  JOB_DATA: (id: string, showResult = true): string => `/jobs/${id}/?show_result=${showResult}`,
  STATIC: (jobId: string, category: string, fileIndex = 0): string =>
    `/static/resources/${jobId}/${category}/${fileIndex}`,
  FILES_CONTENT: (fileId: string): string => `/files/${fileId}/content`,
  PIPELINES: "/pipelines/",
  ALLOW_REGULAR_USERS_LLM_SAMPLES: (id: string): string => `/pipelines/${id}/allow-regular-users-llm-samples/`,
  PIPELINES_VERSION_LATEST: (id: string): string => `/pipelines/${id}/versions/latest/`,
  PIPELINE_VERSION_DRAFT: (id: string): string => `/pipeline-versions/${id}/draft/`,
  PIPELINE_VERSION_TARGETS: (id: string): string => `/pipeline-versions/${id}/targets/`,
  PIPELINE_VERSION_PUBLISH: (id: string): string => `/pipeline-versions/${id}/publish/`,
  PIPELINE_VERSION_DISCARD: (id: string): string => `/pipeline-versions/${id}/discard/`,
  PIPELINE_VERSION_EXTRACTION_CONFIG: (id: string): string => `/pipeline-versions/${id}/extraction-config/`,
  PIPELINE_VERSION_POST_PROCESSORS: (id: string): string => `/pipeline-versions/${id}/post-processors/`,
  MANUAL_CLASSIFICATION_CREATE: "/pipelines/manual-classification/create/",
  MANUAL_CLASSIFICATION_UPDATE: (id: string): string => `/pipelines/manual-classification/${id}/update/`,
  MANUAL_CLASSIFICATION_DELETE: (id: string): string => `/pipelines/manual-classification/${id}/delete/`,
  MANUAL_CLASSIFICATION_VERSION_CONFIG: (id: string): string =>
    `/pipeline-versions/manual-classification/${id}/config/`,
  AUTO_CLASSIFICATION_VERSION_CONFIG: (id: string): string => `/pipeline-versions/auto-classification/${id}/config/`,
  MANUAL_CLASSIFICATION_VERSION_PUBLISH: (id: string): string =>
    `/pipeline-versions/manual-classification/${id}/publish/`,
  MANUAL_CLASSIFICATION_VERSION_DISCARD: (id: string): string =>
    `/pipeline-versions/manual-classification/${id}/discard/`,
  MANUAL_CLASSIFICATION_LATEST_VERSION: (id: string): string =>
    `/pipelines/manual-classification/${id}/versions/latest/`,
  MANUAL_CLASSIFICATION_DRAFT: (id: string): string => `/pipeline-versions/manual-classification/${id}/draft/`,
  AUTO_CLASSIFICATION_DRAFT: (id: string): string => `/pipeline-versions/auto-classification/${id}/draft/`,
  AUTO_CLASSIFICATION_VERSION_PUBLISH: (id: string): string => `/pipeline-versions/auto-classification/${id}/publish/`,
  AUTO_CLASSIFICATION_VERSION_DISCARD: (id: string): string => `/pipeline-versions/auto-classification/${id}/discard/`,
  AUTO_CLASSIFICATION_VERSION_CLASSES: (id: string): string => `/pipeline-versions/auto-classification/${id}/classes/`,
  AUTO_CLASSIFICATION_VERSION_CLASSES_UPDATE_PRIORITY: (id: string): string =>
    `/pipeline-versions/auto-classification/${id}/classes/update-priority/`,
  AUTO_CLASSIFICATION_VERSION_CLASS: (id: string, classId: string): string =>
    `/pipeline-versions/auto-classification/${id}/classes/${classId}/`,
  AUTO_CLASSIFICATION_VERSION_CLASS_KEYWORD_MATCHING: (id: string, classId: string): string =>
    `/pipeline-versions/auto-classification/${id}/classes/${classId}/keyword-matching-config/`,
  TEMPLATES: "/templates/",
  REGISTRY_LLM_MODELS: "/registry/llm-models/",
  LLM_SAMPLES: "/llm-samples/",
  FIXED_TEMPLATES: "/fixed-templates/",
  FIXED_FORM_RERUN: (id: string): string => `/jobs/${id}/fixed-form/rerun/`,
  TEAMS: "/teams/",
  BULK_DELETE_TEAM: "/teams/bulk/",
  CHECK_EMAIL: "/users/check-mail",
  GROUPS: "/groups/",
  STATS: "/stats",
  SYSTEM_SETTINGS: "/system-settings/",
  SYSTEM_SETTINGS_TWO_FA: "/system-settings/2fa/",
  SYSTEM_SETTINGS_IP_WHITELIST: "/system-settings/ip-whitelist/",
  SYSTEM_SETTINGS_API_KEYS: "/system-settings/api-keys/",
  SYSTEM_SETTINGS_API_KEYS_ROTATE: (id: string): string => `/system-settings/api-keys/${id}/rotate/`,
  SYSTEM_SETTINGS_API_KEYS_DELETE: (id: string): string => `/system-settings/api-keys/${id}/`,
  SYSTEM_SETTINGS_TWO_FA_ENABLE: "/system-settings/2fa/enable/",
  SYSTEM_SETTINGS_TWO_FA_DISABLE: "/system-settings/2fa/disable/",
  LICENSE: "/license/",
  LICENSE_USAGE: "/license/usage/",
  RERUN_MULTIPLE_ERROR_JOBS: "/jobs/retry/",
  FORGET_PASSWORD: `${APP_CONFIG.AUTH_API_ROOT}/forgot-password/`,
  RESET_PASSWORD: `${APP_CONFIG.AUTH_API_ROOT}/reset-password/`,
  FEATURE_FLAG: `https://fehub.cinnamon.is/feature-flags/api/fsh/${FSH_FLAG_VERSION}/${APP_CONFIG.APP_ENV}`,
  VERSION: "/version/",
  JOB_PREPROCESSED_PDF: (jobId: string): string => `/jobs/${jobId}/preprocessed-pdf/`,
  JOB_PREPROCESSED_PDF_TRIGGER_BUILD: (jobId: string): string => `/jobs/${jobId}/preprocessed-pdf/trigger-build/`,
  SYSTEM_SETTINGS_RETENTION_POLICIES: "/system-settings/retention-policy/",
  ANNOUNCEMENTS_ACTIVE: "/announcements/active/",
};
